<template>
  <dashboard-page :image-header-props="imageHeaderProps">
    <template v-slot:dashboard>
      <div class="margin-ml">
        <property class="margin-bottom-m" :name="'סוג מינוי'" :value="subscriptionTypeText"/>
        <property
          v-if="userCanceledSubscription || hasScheduledCancellation"
          class="margin-bottom-m"
          :name="'מצב המינוי'"
          :value="'מבוטל'"
          value-color="red"
        />
        <property
          v-if="userCanceledSubscription || hasScheduledCancellation"
          class="margin-bottom-m"
          name="תאריך סיום המינוי"
          :value="isApplePaymentProvider ? nextPaymentDateDisplayValue : scheduledCancellationDateDisplayValue"
        />
        <property
          v-if="!userCanceledSubscription && !hasScheduledCancellation"
          class="margin-bottom-xs"
          :name="'סכום החיוב הבא'"
          :value="currentPrice"
        />
        <div
          v-if="!userCanceledSubscription && !hasScheduledCancellation && currentPrice === '390 ₪'"
          class="promotion-label"
        >
          המחיר הזה נשמר לך לתמיד
        </div>
        <property
          v-if="!userCanceledSubscription && !hasScheduledCancellation"
          class="margin-bottom-xs"
          name="תאריך החיוב הבא"
          :value="nextPaymentDateDisplayValue"
        />
        <property
          v-if="!userCanceledSubscription && !hasScheduledCancellation"
          class="margin-bottom-xxs"
          :name="paymentCardTitle"
          :value="paymentCardText"
        />
        <span
          v-if="isApplePaymentProvider && !isCurrentMemberPaying && !userCanceledSubscription && !hasScheduledCancellation && payingMemberName"
          class="second-member-tag"
        >
          שולם על ידי {{ payingMemberName }}
        </span>
        <span v-if="isApplePaymentProvider && !userCanceledSubscription && !hasScheduledCancellation" class="apple-subscription-note">
          אפשר לערוך את פרטי המינוי דרך Apple Subscriptions
        </span>
        <benefit-notification-box-v2
          class="margin-top-m"
          v-if="hasApplePromotion"
          :flavor="flavors.applePromotion"
          @link-click="onApplePtomotionBoxLinkClick"
          @close="closeModal"
        />
      </div>
      <separator v-if="!isApplePaymentProvider || hasScheduledCancellation" />
      <div class="margin-ml">
        <riseup-button
          v-if="!isApplePaymentProvider && !userCanceledSubscription && !hasScheduledCancellation"
          :action="openUpdateCreditCardPopup"
          :size="'slim'"
          :title="'לעדכן אמצעי תשלום'"
        />
        <riseup-button v-if="hasScheduledCancellation" :action="subscribeClicked" :size="'slim'" :title="'לבחור סוג מינוי'"/>
      </div>
    </template>
  </dashboard-page>
</template>

<script>
import AccountSettingsMixin from '@/pages/responsive-pages/authenticated/navigation-pages/account-settings/AccountSettingsMixin.vue';
import UpdateCreditCardHandler from '@/pages/responsive-pages/authenticated/navigation-pages/account-settings/UpdateCreditCardHandler.vue';
import BaseUI from '@riseupil/base-ui';
import _ from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import BenefitMixin from '@/pages/responsive-pages/authenticated/subscription/V2/BenefitMixin.vue';
import BenefitNotificationBoxV2 from '@/pages/responsive-pages/authenticated/subscription/V2/BenefitNotificationBox2.vue';
import { isIosNativeApp } from '@/utils/deviceUtils';
import ApplePromotionPopup from './components/ApplePromotionPopups/ApplePromotionPopup.vue';
import dates from '../../../../../utils/dates';
import router from '../../../../../router';
import Property from './Property.vue';

export default {
  name: 'SubscribedSettings',
  components: {
    Property,
    DashboardPage: BaseUI.Pages.DashboardPage,
    RiseupButton: BaseUI.RiseupButton,
    Separator: BaseUI.Separator,
    BenefitNotificationBoxV2,
  },
  mixins: [AccountSettingsMixin, UpdateCreditCardHandler, BenefitMixin],
  async created() {
    this.startContentLoading(this.$options.name);
    await this.fetchPricing();
    await this.fetchCreditCardMask();
    this.doneContentLoading(this.$options.name);
  },
  computed: {
    ...mapState('subscription', ['nextPaymentDate', 'price', 'scheduledCancellationDate']),
    ...mapGetters(
      'subscription',
      [
        'newFormattedCardNumberMask',
        'subscriptionTypeText',
        'isApplePaymentProvider',
        'payingMemberId',
        'userCanceledSubscription',
        'hasScheduledCancellation',
      ],
    ),
    ...mapState('session', ['activeMember', 'members']),
    ...mapState('benefits', ['redeemed', 'benefitId']),
    isCurrentMemberPaying() {
      return this.activeMember._id === this.payingMemberId;
    },
    payingMemberName() {
      const payingMember = _.find(this.members, { _id: this.payingMemberId });
      return payingMember ? `${payingMember.firstNamePiiValue} ${payingMember.lastNamePiiValue}` : '';
    },
    nextPaymentDateDisplayValue() {
      return this.nextPaymentDate ? dates.shortDateFormat(this.nextPaymentDate) : 'בבדיקה';
    },
    scheduledCancellationDateDisplayValue() {
      return this.scheduledCancellationDate ? dates.shortDateFormat(this.scheduledCancellationDate) : 'בבדיקה';
    },
    shouldDisableForApplePaymentProvider() {
      return (this.isApplePaymentProvider && !isIosNativeApp())
        || (this.isApplePaymentProvider && this.userCanceledSubscription);
    },
    currentPrice() {
      return `${this.price / 100} ₪`;
    },
    paymentCardText() {
      if (this.newFormattedCardNumberMask != null) {
        return this.newFormattedCardNumberMask;
      }
      if (this.isApplePaymentProvider) {
        return 'App Store';
      }
      return '';
    },
    paymentCardTitle() {
      if (this.isApplePaymentProvider) {
        return 'אמצעי תשלום';
      }
      return 'כרטיס אשראי לחיוב';
    },
    isYearlySubscription() {
      return this.subscriptionTypeText === 'שנתי';
    },
    hasApplePromotion() {
      return this.isApplePaymentProvider && this.benefitId != null && this.redeemed === false;
    },
  },
  methods: {
    ...mapActions('modalRootStore', ['openModal', 'closeModal']),
    ...mapActions('subscription', ['fetchPricing', 'fetchCreditCardMask']),
    ...mapActions('contentLoader', ['startContentLoading', 'doneContentLoading']),
    subscribeClicked() {
      router.push({ path: '/subscription' });
    },
    onApplePtomotionBoxLinkClick() {
      this.openModal({
        component: ApplePromotionPopup,
        props: {
          onClose: () => this.closeModal(),
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@riseupil/base-ui/src/scss/riseup-colors';
@import '~@riseupil/base-ui/src/scss/border-radius';
@import "./settings";

@include properties;

  .mbg-note {
    margin-bottom: 20px;
    margin-top: -18px;
    font-size: 20px;
  }

  .apple-subscription-note {
    display: block;
    color: var(--basics-b-5-b-4-b-8, #B5B4B8);
    text-align: right;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }

  .promotion-label {
    background-color: $riseup_green;
    color: white;
    width: 200px;
    height: 30px;
    border-radius: 8px;
    padding: 5px 12px;
    font-family: SimplerPro,serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0;
    text-align: center;
    vertical-align: middle;
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .second-member-tag {
    display: inline-block;
    color: $riseup_gray_60;
    background: none;
    background-color: $riseup_gray_1;
    text-align: right;
    font-family: SimplerPro, sans-serif;
    font-size: 10.66px;
    font-style: normal;
    font-weight: 700;
    line-height: 12.878px;
    letter-spacing: 0.107px;
    border-radius: $label-border-radius;
    padding: 2px 8px 1px 8px; // visually center, tweak as needed
    margin-bottom: 4px;
    margin-right: 0;
  }
</style>
